import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  RefreshCw, Plus, Pencil, Trash2, X, TrendingUp, TrendingDown,
  Download, AlertCircle, ArrowUp, ArrowDown, Search,
} from 'lucide-react'
import { mfAPI } from '../services/api'
import MF_SEED_TRANSACTIONS from '../data/mfSeedData'
import { computeXIRR, fmtXIRR } from '../utils/xirr'
import toast from 'react-hot-toast'
import LoadingSpinner from './LoadingSpinner'

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) }
  catch { return '—' }
}

const monthsSince = (d) =>
  Math.max(0, (Date.now() - new Date(d).getTime()) / (30 * 24 * 60 * 60 * 1000))

const SOURCE_COLORS = {
  'AngelOne':    'bg-orange-100 text-orange-700',
  'AngelOne PM': 'bg-purple-100 text-purple-700',
  'Zerodha':     'bg-blue-100 text-blue-700',
}
const sourceChip = (s) => SOURCE_COLORS[s] || 'bg-gray-100 text-gray-600'

// ── TxFormModal ───────────────────────────────────────────────────────────────
const SOURCES = ['AngelOne', 'Zerodha', 'AngelOne PM', 'Other']

const EMPTY_FORM = {
  source: 'AngelOne', fundName: '', isin: '',
  date: '', units: '', amount: '', status: 'active', soldNav: '',
}

const TxFormModal = ({ isOpen, onClose, onSave, initial = null }) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (initial) {
      setForm({
        source:   initial.source   || 'AngelOne',
        fundName: initial.fundName || '',
        isin:     initial.isin     || '',
        date:     initial.date ? initial.date.slice(0, 10) : '',
        units:    String(initial.units   || ''),
        amount:   String(initial.amount  || ''),
        status:   initial.status   || 'active',
        soldNav:  initial.soldNav != null ? String(initial.soldNav) : '',
      })
    } else {
      setForm(EMPTY_FORM)
    }
  }, [isOpen, initial])

  if (!isOpen) return null

  const isValid = form.fundName.trim() && form.date && parseFloat(form.units) > 0 && parseFloat(form.amount) > 0
    && (form.status === 'active' || parseFloat(form.soldNav) > 0)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({
        source:   form.source,
        fundName: form.fundName.trim(),
        isin:     form.isin.trim().toUpperCase(),
        date:     form.date,
        units:    parseFloat(form.units),
        amount:   parseFloat(form.amount),
        status:   form.status,
        soldNav:  form.status === 'sold' ? parseFloat(form.soldNav) : null,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-gray-900">
            {initial ? 'Edit Transaction' : 'Add MF Transaction'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="input w-full">
                {SOURCES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="input w-full">
                <option value="active">Active</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fund Name *</label>
            <input type="text" value={form.fundName} onChange={e => setForm({ ...form, fundName: e.target.value })}
              placeholder="e.g. Quant Small Cap Fund" className="input w-full" autoFocus />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ISIN <span className="text-gray-400 font-normal">(for live NAV)</span></label>
            <input type="text" value={form.isin} onChange={e => setForm({ ...form, isin: e.target.value.toUpperCase() })}
              placeholder="e.g. INF966L01689" className="input w-full font-mono text-sm" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Units *</label>
              <input type="number" value={form.units} onChange={e => setForm({ ...form, units: e.target.value })}
                placeholder="0.000" step="0.001" min="0" className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ₹ *</label>
              <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00" step="0.01" min="0" className="input w-full" />
            </div>
          </div>

          {form.status === 'sold' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sold NAV *</label>
              <input type="number" value={form.soldNav} onChange={e => setForm({ ...form, soldNav: e.target.value })}
                placeholder="0.0000" step="0.0001" min="0" className="input w-full" />
            </div>
          )}

          {/* Live preview */}
          {parseFloat(form.units) > 0 && parseFloat(form.amount) > 0 && (() => {
            const units = parseFloat(form.units)
            const amount = parseFloat(form.amount)
            const buyNav = amount / units
            const refNav = form.status === 'sold' ? parseFloat(form.soldNav) || buyNav : buyNav
            const value = units * refNav
            const pnl = value - amount
            return (
              <div className="bg-gray-50 rounded-lg p-3 text-sm grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xs text-gray-400">Buy NAV</p><p className="font-semibold">₹{fmt(buyNav)}</p></div>
                <div><p className="text-xs text-gray-400">{form.status === 'sold' ? 'Sold Value' : 'Invested'}</p><p className="font-semibold">₹{fmt(value, 0)}</p></div>
                <div><p className="text-xs text-gray-400">P&amp;L</p><p className={`font-semibold ${pnl >= 0 ? 'text-success-600' : 'text-danger-600'}`}>{pnl >= 0 ? '+' : ''}₹{fmt(pnl, 0)}</p></div>
              </div>
            )
          })()}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={handleSave} disabled={!isValid || saving} className="btn-primary flex-1 disabled:opacity-50">
            {saving ? <LoadingSpinner size="small" /> : (initial ? 'Save Changes' : 'Add Transaction')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── MFTab ─────────────────────────────────────────────────────────────────────
const MFTab = ({ onSummaryChange }) => {
  const [transactions, setTransactions] = useState([])
  const [navs, setNavs] = useState({})
  const [navUpdatedAt, setNavUpdatedAt] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [navLoading, setNavLoading] = useState(false)
  const [filter, setFilter]     = useState('all')     // 'all' | 'active' | 'sold'
  const [formModal, setFormModal] = useState({ isOpen: false, data: null })

  // ── Loaders ────────────────────────────────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    setLoading(true)
    try {
      const res = await mfAPI.getTransactions()
      const txs = res.data.transactions || []
      setTransactions(txs)
      if (txs.length > 0) fetchNavs(txs)
    } catch (err) {
      console.error('MF load error:', err)
      toast.error('Failed to load mutual fund data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadTransactions() }, [loadTransactions])

  const fetchNavs = async (txs) => {
    const isins = [...new Set(
      (txs || transactions).filter(t => t.status === 'active' && t.isin).map(t => t.isin)
    )]
    if (!isins.length) return
    setNavLoading(true)
    try {
      const res = await mfAPI.getNav(isins)
      setNavs(res.data.navs || {})
      if (res.data.updatedAt) setNavUpdatedAt(res.data.updatedAt)
    } catch (err) {
      console.error('NAV fetch failed:', err)
      toast.error('Could not fetch live NAV — showing purchase NAV')
    } finally {
      setNavLoading(false)
    }
  }

  const handleImportSeed = async () => {
    try {
      await mfAPI.bulkImport(MF_SEED_TRANSACTIONS, true)
      toast.success(`${MF_SEED_TRANSACTIONS.length} transactions imported`)
      loadTransactions()
    } catch (err) {
      toast.error('Import failed')
    }
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSave = async (formData) => {
    try {
      if (formModal.data?._id) {
        await mfAPI.updateTransaction(formModal.data._id, formData)
        toast.success('Transaction updated')
      } else {
        await mfAPI.addTransaction(formData)
        toast.success('Transaction added')
      }
      setFormModal({ isOpen: false, data: null })
      loadTransactions()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save transaction')
      throw err
    }
  }

  const handleDelete = async (id, fundName) => {
    if (!window.confirm(`Remove "${fundName}" transaction?`)) return
    try {
      await mfAPI.deleteTransaction(id)
      toast.success('Transaction removed')
      setTransactions(prev => prev.filter(t => t._id !== id))
    } catch (err) {
      toast.error('Failed to delete transaction')
    }
  }

  // ── Computed rows ──────────────────────────────────────────────────────────
  const computed = useMemo(() => {
    return transactions.map(tx => {
      const buyNav = tx.units > 0 ? tx.amount / tx.units : 0
      const months = monthsSince(tx.date)

      if (tx.status === 'active') {
        const currentNav  = navs[tx.isin] || buyNav
        const navLive     = !!navs[tx.isin]
        const currentValue = tx.units * currentNav
        const pnl          = currentValue - tx.amount
        const pnlPct       = tx.amount > 0 ? (pnl / tx.amount) * 100 : 0
        const target1      = tx.amount * Math.pow(1.01, months) // 1% p.m.
        const target2      = tx.amount * Math.pow(1.02, months) // 2% p.m.
        // Per-lot XIRR: single investment → current value today
        const xirr = computeXIRR([
          { date: tx.date, amount: -tx.amount },
          { date: new Date(), amount: currentValue },
        ])
        return { ...tx, buyNav, currentNav, currentValue, pnl, pnlPct, target1, target2, navLive, xirr }
      } else {
        // sold
        const soldValue  = tx.units * (tx.soldNav || buyNav)
        const pnl        = soldValue - tx.amount
        const pnlPct     = tx.amount > 0 ? (pnl / tx.amount) * 100 : 0
        return { ...tx, buyNav, currentNav: tx.soldNav, currentValue: soldValue, pnl, pnlPct, target1: null, target2: null, navLive: false }
      }
    })
  }, [transactions, navs])

  // ── Summary ────────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const active = computed.filter(t => t.status === 'active')
    const sold   = computed.filter(t => t.status === 'sold')
    const s = {
      activeCount:    active.length,
      activeInvested: active.reduce((s, t) => s + t.amount, 0),
      activeValue:    active.reduce((s, t) => s + t.currentValue, 0),
      activePnl:      active.reduce((s, t) => s + t.pnl, 0),
      soldCount:      sold.length,
      soldInvested:   sold.reduce((s, t) => s + t.amount, 0),
      soldValue:      sold.reduce((s, t) => s + t.currentValue, 0),
      soldPnl:        sold.reduce((s, t) => s + t.pnl, 0),
    }
    s.activePnlPct = s.activeInvested > 0 ? (s.activePnl / s.activeInvested) * 100 : 0
    s.soldPnlPct   = s.soldInvested   > 0 ? (s.soldPnl   / s.soldInvested)   * 100 : 0

    // Overall active XIRR across all active lots
    const xirrFlows = [
      ...active.map(t => ({ date: t.date, amount: -t.amount })),
      ...(s.activeValue > 0 ? [{ date: new Date(), amount: s.activeValue }] : []),
    ]
    s.activeXIRR = computeXIRR(xirrFlows)
    return s
  }, [computed])

  // Notify parent — pass full picture so the summary bar can include sold P&L
  useEffect(() => {
    onSummaryChange?.({
      totalValue:     summary.activeValue,
      totalPnl:       summary.activePnl,
      totalPnlPct:    summary.activePnlPct,
      activeInvested: summary.activeInvested,
      soldPnl:        summary.soldPnl,
      soldInvested:   summary.soldInvested,
    })
  }, [summary, onSummaryChange])

  // ── Sort + search state ────────────────────────────────────────────────────
  const [mfSortKey, setMfSortKey] = useState('date')
  const [mfSortDir, setMfSortDir] = useState('desc')   // newest first
  const [mfSearch,  setMfSearch]  = useState('')

  const MfSortHeader = ({ field, label, right = false }) => {
    const active = mfSortKey === field
    return (
      <th
        className={`px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors ${right ? 'text-right' : 'text-left'}`}
        onClick={() => { if (mfSortKey === field) setMfSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setMfSortKey(field); setMfSortDir('asc') } }}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active
            ? (mfSortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary-500" /> : <ArrowDown className="w-3 h-3 text-primary-500" />)
            : <ArrowUp className="w-3 h-3 text-gray-300" />}
        </span>
      </th>
    )
  }

  // Filtered + sorted display
  const filtered = useMemo(() => {
    const base = filter === 'active' ? computed.filter(t => t.status === 'active')
      : filter === 'sold' ? computed.filter(t => t.status === 'sold')
      : computed
    const q = mfSearch.toLowerCase()
    const searched = q ? base.filter(t => (t.fundName || t.schemeName || '').toLowerCase().includes(q)) : base
    return [...searched].sort((a, b) => {
      let va = a[mfSortKey] ?? 0, vb = b[mfSortKey] ?? 0
      if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase() }
      return mfSortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
    })
  }, [computed, filter, mfSearch, mfSortKey, mfSortDir])

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center py-16"><LoadingSpinner size="large" /></div>
  }

  if (!transactions.length) {
    return (
      <div className="text-center py-16 space-y-4">
        <AlertCircle className="w-12 h-12 mx-auto text-gray-300" />
        <p className="text-gray-500 font-medium">No mutual fund transactions yet</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={handleImportSeed}
            className="flex items-center gap-2 btn-primary text-sm"
          >
            <Download className="w-4 h-4" /> Import Initial Data ({MF_SEED_TRANSACTIONS.length} transactions)
          </button>
          <button
            onClick={() => setFormModal({ isOpen: true, data: null })}
            className="flex items-center gap-2 btn-secondary text-sm"
          >
            <Plus className="w-4 h-4" /> Add Manually
          </button>
        </div>
        <TxFormModal isOpen={formModal.isOpen} onClose={() => setFormModal({ isOpen: false, data: null })} onSave={handleSave} />
      </div>
    )
  }

  const Pnl = ({ val, pct, size = 'sm' }) => {
    const pos = val >= 0
    const cls = pos ? 'text-success-600' : 'text-danger-600'
    return (
      <div className={cls}>
        <span className={`font-semibold text-${size}`}>{pos ? '+' : ''}₹{fmt(val, 0)}</span>
        <span className="text-xs opacity-75 ml-1">({pos ? '+' : ''}{fmt(pct)}%)</span>
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* ── Holdings ── */}
      {<>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Active */}
        <div className="bg-gradient-to-br from-success-50 to-emerald-50 border border-success-100 rounded-xl p-4">
          <p className="text-xs font-medium text-success-700 uppercase tracking-wide mb-2">
            Active Holdings <span className="normal-case font-normal text-success-600">({summary.activeCount})</span>
          </p>
          <p className="text-xl font-bold text-gray-900">₹{fmt(summary.activeValue, 0)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Invested: ₹{fmt(summary.activeInvested, 0)}</p>
          <Pnl val={summary.activePnl} pct={summary.activePnlPct} />
          {summary.activeXIRR != null && (
            <p className={`text-xs font-semibold mt-1 ${summary.activeXIRR >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              XIRR {fmtXIRR(summary.activeXIRR)} p.a.
            </p>
          )}
          {navLoading && <span className="text-xs text-gray-400 mt-1 block">Fetching live NAV…</span>}
        </div>

        {/* Sold */}
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
            Sold Transactions <span className="normal-case font-normal text-gray-500">({summary.soldCount})</span>
          </p>
          <p className="text-xl font-bold text-gray-900">₹{fmt(summary.soldValue, 0)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Invested: ₹{fmt(summary.soldInvested, 0)}</p>
          <Pnl val={summary.soldPnl} pct={summary.soldPnlPct} />
        </div>

        {/* Overall */}
        <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-100 rounded-xl p-4">
          <p className="text-xs font-medium text-primary-700 uppercase tracking-wide mb-2">Overall P&amp;L</p>
          <Pnl val={summary.activePnl + summary.soldPnl} pct={
            (summary.activeInvested + summary.soldInvested) > 0
              ? ((summary.activePnl + summary.soldPnl) / (summary.activeInvested + summary.soldInvested)) * 100
              : 0
          } size="xl" />
          <p className="text-xs text-gray-500 mt-1">
            Total invested: ₹{fmt(summary.activeInvested + summary.soldInvested, 0)}
          </p>
          {navUpdatedAt && (
            <p className="text-xs text-gray-400 mt-1">NAV as of {fmtDate(navUpdatedAt)}</p>
          )}
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Filter tabs + search */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            {[['all', 'All'], ['active', 'Active'], ['sold', 'Sold']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  filter === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              value={mfSearch} onChange={e => setMfSearch(e.target.value)}
              placeholder="Search fund…" className="input pl-8 py-1.5 text-sm w-44"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchNavs()}
            disabled={navLoading}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${navLoading ? 'animate-spin' : ''}`} />
            Refresh NAV
          </button>
          <button
            onClick={() => setFormModal({ isOpen: true, data: null })}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Transaction
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap">Source</th>
              <MfSortHeader field="fundName"      label="Fund" />
              <MfSortHeader field="date"          label="Date"     right />
              <MfSortHeader field="units"         label="Units"    right />
              <th className="text-right px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap">Buy NAV</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap">Cur NAV</th>
              <MfSortHeader field="amount"        label="Invested" right />
              <MfSortHeader field="currentValue"  label="Cur Val"  right />
              <MfSortHeader field="pnl"           label="P&L"      right />
              <MfSortHeader field="returnPct"     label="Ret%"     right />
              <MfSortHeader field="xirr"          label="XIRR"     right />
              <th className="text-right px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap" title="Value at 1%/month compound since purchase">1% Tgt</th>
              <th className="text-right px-3 py-2.5 font-medium text-gray-600 whitespace-nowrap" title="Value at 2%/month compound since purchase">2% Tgt</th>
              <th className="text-center px-3 py-2.5 font-medium text-gray-600">Status</th>
              <th className="text-center px-3 py-2.5 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={15} className="py-10 text-center text-gray-400 text-sm">No transactions match this filter.</td>
              </tr>
            )}
            {filtered.map((tx) => {
              const pnlPos = tx.pnl >= 0
              const navAbove = tx.status === 'active' && tx.currentNav > tx.buyNav
              return (
                <tr key={tx._id} className={`hover:bg-gray-50 transition-colors ${tx.status === 'sold' ? 'opacity-75' : ''}`}>
                  {/* Source */}
                  <td className="px-3 py-2.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${sourceChip(tx.source)}`}>
                      {tx.source}
                    </span>
                  </td>

                  {/* Fund */}
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-gray-800 leading-tight max-w-[160px]" title={tx.fundName}>
                      {tx.fundName}
                    </div>
                    {tx.isin && <div className="text-xs text-gray-400 font-mono mt-0.5">{tx.isin}</div>}
                  </td>

                  {/* Date */}
                  <td className="px-3 py-2.5 text-center text-gray-600 whitespace-nowrap">{fmtDate(tx.date)}</td>

                  {/* Units */}
                  <td className="px-3 py-2.5 text-right font-mono text-xs text-gray-700">{tx.units?.toFixed(3)}</td>

                  {/* Buy NAV */}
                  <td className="px-3 py-2.5 text-right text-gray-600">₹{fmt(tx.buyNav)}</td>

                  {/* Current / Sold NAV */}
                  <td className="px-3 py-2.5 text-right font-medium">
                    {tx.status === 'active' ? (
                      <span className={tx.navLive ? (navAbove ? 'text-success-600' : 'text-danger-600') : 'text-gray-400'}>
                        ₹{fmt(tx.currentNav)}
                        {!tx.navLive && tx.isin && <span className="text-xs ml-0.5 opacity-60">*</span>}
                      </span>
                    ) : (
                      <span className="text-gray-600">₹{fmt(tx.soldNav)}</span>
                    )}
                  </td>

                  {/* Invested */}
                  <td className="px-3 py-2.5 text-right text-gray-700">₹{fmt(tx.amount, 0)}</td>

                  {/* Current Value */}
                  <td className="px-3 py-2.5 text-right font-medium text-gray-800">₹{fmt(tx.currentValue, 0)}</td>

                  {/* P&L */}
                  <td className={`px-3 py-2.5 text-right font-medium ${pnlPos ? 'text-success-600' : 'text-danger-600'}`}>
                    <div className="flex items-center justify-end gap-0.5">
                      {pnlPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {pnlPos ? '+' : ''}₹{fmt(tx.pnl, 0)}
                    </div>
                  </td>

                  {/* Return % */}
                  <td className={`px-3 py-2.5 text-right font-medium ${pnlPos ? 'text-success-600' : 'text-danger-600'}`}>
                    {pnlPos ? '+' : ''}{fmt(tx.pnlPct)}%
                  </td>

                  {/* XIRR */}
                  <td className={`px-3 py-2.5 text-right text-xs font-semibold ${
                    tx.status !== 'active' ? 'text-gray-400' :
                    tx.xirr == null ? 'text-gray-400' :
                    tx.xirr >= 0 ? 'text-success-600' : 'text-danger-600'
                  }`}>
                    {tx.status === 'active' ? fmtXIRR(tx.xirr) : '—'}
                  </td>

                  {/* 1% Target */}
                  <td className="px-3 py-2.5 text-right text-xs text-gray-500">
                    {tx.target1 != null ? `₹${fmt(tx.target1, 0)}` : '—'}
                  </td>

                  {/* 2% Target */}
                  <td className="px-3 py-2.5 text-right text-xs text-gray-500">
                    {tx.target2 != null ? `₹${fmt(tx.target2, 0)}` : '—'}
                  </td>

                  {/* Status */}
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      tx.status === 'active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {tx.status === 'active' ? 'Active' : 'Sold'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => setFormModal({ isOpen: true, data: tx })}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(tx._id, tx.fundName)}
                        className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>

          {/* Footer totals */}
          {filtered.length > 0 && (() => {
            const activeFil = filtered.filter(t => t.status === 'active')
            const soldFil   = filtered.filter(t => t.status === 'sold')
            const totValue   = filtered.reduce((s, t) => s + t.currentValue, 0)
            const totInvest  = filtered.reduce((s, t) => s + t.amount, 0)
            const totPnl     = filtered.reduce((s, t) => s + t.pnl, 0)
            const totPnlPos  = totPnl >= 0
            return (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
                  <td colSpan={6} className="px-3 py-2.5 text-xs text-gray-500">
                    {filtered.length} row{filtered.length !== 1 ? 's' : ''}
                    {activeFil.length > 0 && soldFil.length > 0 && ` (${activeFil.length} active · ${soldFil.length} sold)`}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-800">₹{fmt(totInvest, 0)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-900">₹{fmt(totValue, 0)}</td>
                  <td className={`px-3 py-2.5 text-right ${totPnlPos ? 'text-success-600' : 'text-danger-600'}`}>
                    {totPnlPos ? '+' : ''}₹{fmt(totPnl, 0)}
                  </td>
                  <td className={`px-3 py-2.5 text-right ${totPnlPos ? 'text-success-600' : 'text-danger-600'}`}>
                    {totInvest > 0 ? `${totPnlPos ? '+' : ''}${fmt(totPnl / totInvest * 100)}%` : '—'}
                  </td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            )
          })()}
        </table>
      </div>

      {navs && Object.values(navs).some(v => v === null) && (
        <p className="text-xs text-gray-400">
          * NAV not available in AMFI for some ISINs — showing purchase NAV as fallback
        </p>
      )}

      {/* Re-import */}
      <div className="flex justify-end">
        <button
          onClick={handleImportSeed}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
          title="Replaces all transactions with the initial seed data"
        >
          <Download className="w-3 h-3" /> Re-import initial data
        </button>
      </div>

      {/* Form modal */}
      <TxFormModal
        isOpen={formModal.isOpen}
        initial={formModal.data}
        onClose={() => setFormModal({ isOpen: false, data: null })}
        onSave={handleSave}
      />
      </>}
    </div>
  )
}

export default MFTab
